
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNotify } from '@/hooks/useNotify';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from '@/components/ui/label';
import { InfoIcon } from 'lucide-react';

interface CostCenterSettings {
  id: string;
  company_id: string;
  default_markup: number;
  default_margin: number;
}

const MarkupSettings = () => {
  const { user, profile } = useAuth();
  const notify = useNotify();
  const [settings, setSettings] = useState<CostCenterSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [markup, setMarkup] = useState('0');
  const [margin, setMargin] = useState('0');
  const [activeTab, setActiveTab] = useState('markup');

  useEffect(() => {
    if (profile?.company_id) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [profile]);

  const fetchSettings = async () => {
    if (!profile?.company_id) {
      notify.warning("Missing Company", "You must be associated with a company to view settings.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('cost_center_settings')
        .select('*')
        .eq('company_id', profile.company_id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setSettings(data);
        setMarkup(data.default_markup.toString());
        setMargin(data.default_margin.toString());
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!profile?.company_id) {
      notify.warning("Missing Company", "You must be associated with a company to save settings.");
      return;
    }

    try {
      const markupValue = parseFloat(markup) || 0;
      const marginValue = parseFloat(margin) || 0;
      
      if (settings) {
        const { error } = await supabase
          .from('cost_center_settings')
          .update({
            default_markup: markupValue,
            default_margin: marginValue
          })
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('cost_center_settings')
          .insert({
            company_id: profile.company_id,
            default_markup: markupValue,
            default_margin: marginValue
          });

        if (error) throw error;
      }

      notify.success("Settings saved successfully");
      
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      notify.error(error);
    }
  };

  const calculateMarginFromMarkup = (markupPercentage: string) => {
    const markup = parseFloat(markupPercentage) || 0;
    const margin = (markup / (100 + markup)) * 100;
    return margin.toFixed(2);
  };

  const calculateMarkupFromMargin = (marginPercentage: string) => {
    const margin = parseFloat(marginPercentage) || 0;
    const markup = (margin / (100 - margin)) * 100;
    return markup.toFixed(2);
  };

  const handleMarkupChange = (value: string) => {
    setMarkup(value);
    if (activeTab === 'markup') {
      setMargin(calculateMarginFromMarkup(value));
    }
  };

  const handleMarginChange = (value: string) => {
    setMargin(value);
    if (activeTab === 'margin') {
      setMarkup(calculateMarkupFromMargin(value));
    }
  };

  if (!profile?.company_id) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Markup & Margin Defaults</CardTitle>
          <CardDescription>Set your default markup and margin percentages</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-warning">You must be associated with a company to manage markup and margin settings.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Default Markup & Margin</CardTitle>
        <CardDescription>Set your default markup and margin percentages</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="bg-warning/10 border border-warning/30 p-4 rounded-md mb-6">
          <div className="flex gap-3">
            <InfoIcon className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-warning-foreground font-medium mb-1">Category-specific markup/margin settings</p>
              <p className="text-muted-foreground text-sm">
                You can now set specific markup/margin tiers for each category in the Categories tab. 
                The values below serve as defaults when category-specific settings are not available.
              </p>
            </div>
          </div>
        </div>
        
        <Tabs 
          defaultValue="markup" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="markup">Markup</TabsTrigger>
            <TabsTrigger value="margin">Margin</TabsTrigger>
          </TabsList>
          
          <TabsContent value="markup" className="mt-4 space-y-4">
            <div>
              <Label htmlFor="markup">Default Markup Percentage</Label>
              <div className="flex items-center mt-2">
                <Input 
                  id="markup"
                  type="number"
                  min="0"
                  step="0.01"
                  value={markup}
                  onChange={(e) => handleMarkupChange(e.target.value)}
                  className="w-24"
                />
                <span className="ml-2">%</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                This markup is equivalent to a {margin}% margin.
              </p>
            </div>
          </TabsContent>
          
          <TabsContent value="margin" className="mt-4 space-y-4">
            <div>
              <Label htmlFor="margin">Default Margin Percentage</Label>
              <div className="flex items-center mt-2">
                <Input 
                  id="margin"
                  type="number"
                  min="0"
                  max="99.99"
                  step="0.01"
                  value={margin}
                  onChange={(e) => handleMarginChange(e.target.value)}
                  className="w-24"
                />
                <span className="ml-2">%</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                This margin is equivalent to a {markup}% markup.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button onClick={saveSettings} disabled={loading}>
          Save Settings
        </Button>
      </CardFooter>
    </Card>
  );
};

export default MarkupSettings;
